import React from 'react';
import {
    Progress,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faEye } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    FormCheckbox,
    PageHeading,
    PageWrap,
    toasty,
    onFieldChange,
    IconButton,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { NotAuthorizedPage } from '../status/StatusCodes';
import SlideForm from '../common/forms/SlideForm';
import BillingIssuesWidget from '../widgets/BillingIssuesWidget';
import TimesheetDetails from '../timesheet/TimesheetDetails';

class BillingIssuesIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();
        this.invoiceSlideoutRef = React.createRef();
        this.billingIssuesSlideoutRef = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            selectedDetails: [],
            loadingReport: false,
            isApproving: false,
            isInvoicing: false,
            createJob: false,
            showBillingIssuesSlideout: false,
            billingIssueMessages: [],
            invoiceTimesheetIds: [],
            currentTimesheetIndex: 0,
        };

        this.onChange = this.onChange.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onBillingIssuesSlideout = async (id) => {
        const messages = await httpClient.js(
            ApiRoutes.billingIssues.messages(id),
        );

        this.setState({
            billingIssueMessages: messages,
            showBillingIssuesSlideout: true,
        });
    };

    onChange = onFieldChange;

    onNavBack = async () => {
        let { invoiceTimesheetIds, currentTimesheetIndex } = this.state;

        if (currentTimesheetIndex == 0) return;

        if (invoiceTimesheetIds && invoiceTimesheetIds.length > 0) {
            await this.context.setFormOpened(true);

            currentTimesheetIndex -= 1;
            if (currentTimesheetIndex < 0) currentTimesheetIndex = 0;

            const timesheetId = invoiceTimesheetIds[currentTimesheetIndex];
            this.timesheetDetailsRef.current.open(timesheetId);

            this.setState({ currentTimesheetIndex });
        }
    };

    onNavForward = async () => {
        let { invoiceTimesheetIds, currentTimesheetIndex } = this.state;

        if (invoiceTimesheetIds && invoiceTimesheetIds.length > 0) {
            await this.context.setFormOpened(true);

            if (currentTimesheetIndex + 1 >= invoiceTimesheetIds.length) return;

            currentTimesheetIndex += 1;

            const timesheetId = invoiceTimesheetIds[currentTimesheetIndex];
            this.timesheetDetailsRef.current.open(timesheetId);

            this.setState({ currentTimesheetIndex });
        }
    };

    onReplyToBillingIssues = async (id, message, updateStatus) => {
        if (message == null || message.length == 0) {
            toasty.error('Please supply reply notes');
            return;
        }

        const model = {
            timesheetId: id,
            notes: message,
            updateStatus,
        };

        await httpClient.post(ApiRoutes.billingIssues.reply(), model);

        toasty.success('Billing issue reply successful');

        this.setState({
            showBillingIssuesSlideout: false,
        });

        this.state.gridOptions.refresh();
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    openTimesheetDetails = async (row) => {
        if (row) {
            if (row.invoiceId && row.invoiceTimesheetIds) {
                const invoiceTimesheetIds = [];

                for (const potentialTimesheetId of row.invoiceTimesheetIds.split(
                    ',',
                )) {
                    if (potentialTimesheetId) {
                        const timesheetId = potentialTimesheetId.trim();

                        if (timesheetId) {
                            invoiceTimesheetIds.push(timesheetId);
                        }
                    }
                }

                this.setState({ invoiceTimesheetIds }, async () => {
                    await this.context.setFormOpened(true);
                    this.timesheetDetailsRef.current.open(
                        invoiceTimesheetIds[0],
                    );
                });

                return;
            }

            this.setState({ timesheetList: [], currentTimesheetIndex: 0 });

            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(row.timesheetId);
        }
    };

    populateState = async () => {
        let [dispatchLocations, billingStatuses] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.billingStatuses()),
        ]);

        const initialListFilters = {
            initialDispatchId: '',
            initialCustomerCompanyId: 0,
            initialOperationsCenterId: 0,
            initialBillingStatusId: 10,
            initialPayrollStatusId: 0,
            initialHasOverride: null,
        };

        billingStatuses = billingStatuses.filter(
            (x) => x.value == 10 || x.value == 9,
        );

        billingStatuses.push({ label: 'Accepted', value: -99 });

        const gridOptions = createGridOptions(this);
        const context = this;

        // https://www.ag-grid.com/documentation/javascript/row-selections#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        gridOptions.rowSelection = 'single';

        gridOptions.postProcessData = this.transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.rowClassRules = {};

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
        };

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.dispatchId,
        };

        const billingStatusFilterParams = {
            suppressFilterButton: true,
            options: billingStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.initialBillingStatusId,
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
                // floatingFilterComponentParams: {
                //    suppressFilterButton: true,
                // }
            },
            {
                colId: 'customerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ContractNumber', // leave this, it doesnt map to an ef prop but is triggered for the sort on the dynamic field.
                headerName: 'Contract #',
                field: 'contractNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DispatchLocationId',
                headerName: 'Dispatching',
                field: 'dispatchOfficeName',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'Invoice.InvoiceNumber',
                headerName: 'Invoice',
                field: 'invoiceNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Timesheet.TimesheetNumber',
                headerName: 'Timesheets',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'JobLocationAddress',
                headerName: 'Job Location',
                field: 'jobAddress',
                sortable: true,
                flex: 2.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Timesheet.WorkOrderNumber',
                headerName: 'Work Order',
                field: 'workOrderNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'BillingStatusId',
                headerName: 'Status',
                field: 'billingStatus',
                sortable: true,
                flex: 1,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billingStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billingStatusFilterParams,
            },
            {
                colId: 'Id',
                flex: 2,
                maxWidth: 150,
                headerName: 'Billing Issue',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data) {
                        return (
                            <IconButton
                                title="Billing Issue"
                                type="button"
                                onClick={() => {
                                    this.onBillingIssuesSlideout(
                                        params.data.timesheetId,
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faComments} />
                            </IconButton>
                        );
                    }

                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 2,
                maxWidth: 150,
                headerName: 'View Timesheets',
                sortable: false,
                cellRenderer: (params) => {
                    if (!params?.data) return null;

                    const { invoiceTimesheetIds } = params.data;
                    const hasInvoiceTimesheets = (invoiceTimesheetIds || '')
                        .split(',')
                        .map((x) => x.trim())
                        .filter(Boolean).length > 0;

                    if (!hasInvoiceTimesheets) return null;

                    return (
                        <IconButton
                            title="View Timesheet"
                            type="button"
                            onClick={() => this.openTimesheetDetails(params.data)}
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </IconButton>
                    );
                },
            },
        ];

        /// ///////////////////////////////////////////////////////
        // RJP - Cache and reload filter and sort changes
        gridOptions.onFilterChanged = () => { };

        gridOptions.onSortChanged = () => { };

        gridOptions.onFirstDataRendered = () => { };

        gridOptions.onGridReady = (params) => {
            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        // END - Cache and reload filter and sort changes
        /// //////////////////////////////////////////////////////

        const dataSource = createDataSource(
            ApiRoutes.billingIssues.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
        });
    };

    renderTimesheetByjobList = () => {
        const { allTimesheetsByJob } = this.state;

        const groupContainsInvoice = allTimesheetsByJob.filter((e) => e.invoiceStatusId > 0).length > 0;
        const groupMixSelected = allTimesheetsByJob.filter((e) => e.convert == false).length > 0;

        return allTimesheetsByJob.map((timesheet) => {
            const tsHasInvoice = timesheet.invoiceStatusId > 0;

            return (
                <tr key={allTimesheetsByJob}>
                    <td>
                        {tsHasInvoice && (
                            <FormCheckbox
                                id={timesheet.id}
                                checked={false}
                                labelText={timesheet.timesheetNumber}
                                disabled
                            />
                        )}
                        {!tsHasInvoice && (
                            <FormCheckbox
                                id={timesheet.id}
                                checked={timesheet.convert}
                                onChange={(e) => {
                                    const { allTimesheetsByJob } = this.state;
                                    timesheet.convert = e.target.checked;
                                    this.setState({
                                        allTimesheetsByJob,
                                    });
                                }}
                                labelText={timesheet.timesheetNumber}
                            />
                        )}
                    </td>
                    <td>
                        {timesheet.convert && (
                            <>
                                {!tsHasInvoice
                                    && (groupContainsInvoice
                                        || groupMixSelected) && (
                                        <span>
                                            <ul
                                                style={{
                                                    paddingLeft: '18px',
                                                    marginBottom: '0px',
                                                }}
                                            >
                                                <li>New job</li>
                                                <li>New timesheet number</li>
                                            </ul>
                                        </span>
                                    )}
                                {!tsHasInvoice
                                    && !groupContainsInvoice
                                    && !groupMixSelected && (
                                        <span>
                                            <ul
                                                style={{
                                                    paddingLeft: '18px',
                                                    marginBottom: '0px',
                                                }}
                                            >
                                                <li>Same job</li>
                                                <li>New timesheet number</li>
                                            </ul>
                                        </span>
                                    )}
                            </>
                        )}
                        {!timesheet.convert && (
                            <>
                                {tsHasInvoice && <span>(unavailable)</span>}
                                {!tsHasInvoice && <span>(none)</span>}
                            </>
                        )}
                    </td>
                    <td>{timesheet.timesheetStatus}</td>
                    <td>{timesheet.billingStatus}</td>
                    <td>{timesheet.invoiceStatus}</td>
                </tr>
            );
        });
    };

    render() {
        const {
            rowData,
            gridOptions,
            selectedRow,
            billingIssueMessages,
            loading,
        } = this.state;

        const { tenantSettings } = this.context.tenant;

        if (loading || !tenantSettings) {
            return (
                <Progress />
            );
        }

        if (!tenantSettings.billingEnabled || !tenantSettings.invoicingEnabled) {
            return <NotAuthorizedPage />;
        }

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faComments}
                        className="mr-2 text-muted"
                    />
                    <span>Billing Issues</span>
                </PageHeading>

                <DataGridToolbar
                    entity="BillingIssues"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath:
                            ApiRoutes.billingIssues.excelExport(),
                        filePrefix: 'BillingIssues',
                    }}
                    serverExportDisabled={
                        !!this.state.saving
                        || !!this.state.loading
                        || !!this.state.loadingData
                    }
                />
                {!!this.state.loadingReport && (
                    <CustomCircularProgress />
                )}

                {!this.state.loadingReport && (
                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                )}

                <SlideForm
                    loading={false}
                    show={this.state.showBillingIssuesSlideout}
                    id="billingIssuesSlideout"
                    formIcon={faComments}
                    formTitle="Billing Issues"
                    onClose={() => {
                        this.setState({
                            showBillingIssuesSlideout: false,
                        });
                    }}
                >
                    <BillingIssuesWidget
                        ref={this.billingIssuesSlideoutRef}
                        billing={selectedRow}
                        messages={billingIssueMessages}
                        context="replying"
                        onReplyCallback={(
                            id,
                            message,
                            updateStatus,
                        ) => {
                            this.onReplyToBillingIssues(
                                id,
                                message,
                                updateStatus,
                            );
                        }}
                        onCloseCallback={() => {
                            this.setState({
                                showBillingIssuesSlideout: false,
                            });
                        }}
                    />
                </SlideForm>

                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    toggleShow={(open) => this.setState({
                        timesheetDetailsOpen: open,
                    })}
                    onClose={this.onTimesheetDetailsClosed}
                    onAccept={this.onAcceptTimesheet}
                    onReject={this.onRejectTimesheet}
                    onlyBillable
                    isBilling
                    showNav
                    onNavBack={this.onNavBack}
                    onNavForward={this.onNavForward}
                />
            </PageWrap>
        );
    }
}

export default BillingIssuesIndex;
